import RemoteRuntime from '../RemoteRuntime';
import { requestIdleCallback } from '../utils/idle';
import { isDev } from '../utils/is-dev';
import { getSlardar } from '../utils/slardar';
import { getContainerName } from '../utils/get-container-name';
import { IGlobalManifestMap, IRemoteModuleManifest, IServicesDiscovered } from '../types';
import RemoteModuleHandler from '../RemoteModuleHandler';
import PrefetchModuleHandler from '../PrefetchModuleHandler';
import { REMOTE_MODULE_MANIFEST_PATH, hostManifestMap } from '../const';
import PreloadEntryHandler from '../PreloadEntryHandler';
import EventEmitter, { EventPayload } from '../EventEmitter';

interface IRemoteRuntimeRoot {
  __MEEGO_REMOTE_RUNTIME__?: RemoteRuntime;
  __MEEGO_REMOTE_MANIFESTS_SSG__?: IGlobalManifestMap;
  __MEEGO_REMOTE_MANIFESTS_INTERNAL__?: IGlobalManifestMap;
}

export enum ELoadStrategy {
  // 首次被声明时立刻加载
  Instantly,
  // 空闲时加载
  Idle,
  // 使用到的时候再加载
  Lazy,
}

interface ILoadStrategy {
  manifest?: ELoadStrategy;
  entry?: ELoadStrategy;
  chunk?: ELoadStrategy;
}

export interface IRemoteModuleManagerOptions {
  getRoot?: () => IRemoteRuntimeRoot;
  servicesDiscovered: IServicesDiscovered;

  loadStrategy?: ILoadStrategy;
}
const defaultLoadStrategy: ILoadStrategy = {
  manifest: ELoadStrategy.Instantly,
  entry: ELoadStrategy.Idle,
  chunk: ELoadStrategy.Lazy,
};

const getRootDefault = () => window as IRemoteRuntimeRoot;
const IDLE_TIMEOUT: number = 3000;
const MAX_ERROR_EVENT_COUNT: number = 20;

export default class RemoteModuleManager {
  // runtime 挂载的 root
  private readonly getRoot: () => IRemoteRuntimeRoot;
  // 已被发现的服务，key 是 container 名，value 是获取 manifest 的 url
  private readonly servicesDiscovered: IServicesDiscovered;
  // 加载策略，对 manifest、entry、chunk 分别设置
  private readonly loadStrategy: ILoadStrategy;

  private readonly remoteModuleHandlers: Map<string, RemoteModuleHandler> = new Map();

  private readonly prefetchModuleHandlers: Map<string, PrefetchModuleHandler> = new Map();

  // Entry 预加载逻辑
  private preloadEntryHandler: PreloadEntryHandler;
  private eventEmitter = new EventEmitter();

  constructor(options: IRemoteModuleManagerOptions) {
    this.getRoot = options.getRoot || getRootDefault;
    this.servicesDiscovered = options.servicesDiscovered;
    this.loadStrategy = options.loadStrategy || defaultLoadStrategy;

    // 将 servicesDiscovered 写回 runtime
    const runtime = this.getRuntime();
    Object.assign(runtime.servicesDiscovered, this.servicesDiscovered);

    const { registry } = runtime;

    // 将 host manifest 初始化
    Object.entries(hostManifestMap).forEach(([container, manifest]) => {
      registry.register(getContainerName(manifest, container), manifest);
    });

    // 将代码中定义的 manifest 初始化
    const manifestInternal =
      this.getRoot()?.__MEEGO_REMOTE_MANIFESTS_INTERNAL__ ||
      window.__MEEGO_REMOTE_MANIFESTS_INTERNAL__;
    if (manifestInternal) {
      Object.entries(manifestInternal).forEach(([container, manifest]) => {
        registry.register(getContainerName(manifest, container), manifest);
      });
    }

    // 将 html 中注入的 manifest 初始化
    const manifestsSSG =
      this.getRoot()?.__MEEGO_REMOTE_MANIFESTS_SSG__ || window.__MEEGO_REMOTE_MANIFESTS_SSG__;
    if (manifestsSSG) {
      Object.entries(manifestsSSG).forEach(([container, manifest]) => {
        registry.register(getContainerName(manifest, container), manifest);
      });
    }

    // 增加全局 script 执行 error 处理，包括了 script src 加载和 inline script
    window?.addEventListener(
      'error',
      event => {
        if (event instanceof ErrorEvent && event.type === 'error') {
          const { error, filename = '', lineno = 0, colno = 0, timeStamp = 0 } = event;
          // 只保留最新的 ErrorEvent
          while (runtime.scriptErrors.length > MAX_ERROR_EVENT_COUNT) {
            runtime.scriptErrors.shift();
          }
          // 有用的信息取出来放到 runtime 中
          runtime.scriptErrors.push({
            filename,
            lineno,
            colno,
            timeStamp,
            error: {
              message: error?.message || '',
              stack: error?.stack || '',
            },
          });
        }
      },
      true,
    );

    // 触发 manifest 和 entry 的加载，数据可能不全
    this.loadManifestsByStrategy().then(() => {
      // 剩余 manifests 加载完之后，再触发一次 entry 加载
      this.loadEntriesByStrategy();
    });
    this.loadEntriesByStrategy();
  }

  // 获取 runtime 实例
  getRuntime = () => {
    const root = this.getRoot();
    root.__MEEGO_REMOTE_RUNTIME__ = root.__MEEGO_REMOTE_RUNTIME__ || new RemoteRuntime();
    return root.__MEEGO_REMOTE_RUNTIME__;
  };

  // 找到未加载的 manifest keys
  private getUnloadedManifestKeys = () => {
    const { registry } = this.getRuntime();
    return Object.keys(this.servicesDiscovered).filter(k => !registry.has(k));
  };

  // 找到未加载的 entry keys
  private getUnloadedEntryKeys = () => {
    const { registry, containers } = this.getRuntime();
    const r: string[] = [];
    for (const key of registry.keys()) {
      if (!containers[key]) {
        r.push(key);
      }
    }
    return r;
  };

  /** 流程相关 **/
  // 根据策略加载 manifest
  private loadManifestsByStrategy = () => {
    switch (this.loadStrategy?.manifest) {
      // 立刻加载
      case ELoadStrategy.Instantly: {
        return Promise.all(this.getUnloadedManifestKeys().map(k => () => this.loadManifest(k)));
      }
      // 空闲加载
      case ELoadStrategy.Idle: {
        // requestIdleCallback(this.runTaskQueue, { timeout: IDLE_TIMEOUT });
        return this.requestIdleLoadManifest(this.getUnloadedManifestKeys());
      }
      // 等待按需加载
      case ELoadStrategy.Lazy:
      default: {
        // do nothing
        return Promise.resolve();
      }
    }
  };

  // 根据策略加载 entry js
  private loadEntriesByStrategy = () => {
    switch (this.loadStrategy?.entry) {
      // 立刻加载
      case ELoadStrategy.Instantly: {
        this.getUnloadedEntryKeys().map(k => this.loadRemoteEntry(k));
        break;
      }
      // 空闲加载
      case ELoadStrategy.Idle: {
        this.eventEmitter.once('preloadEntry', this.idlePreloadEntry);
        break;
      }
      // 等待按需加载
      case ELoadStrategy.Lazy:
      default: {
        // do nothing
      }
    }
  };

  /** 加载相关 **/
  getHandler = (container: string) => {
    let handler = this.remoteModuleHandlers.get(container);
    if (!handler) {
      handler = new RemoteModuleHandler({ container, runtime: this.getRuntime() });
      this.remoteModuleHandlers.set(container, handler);
    }
    return handler;
  };

  // prefetch 的 handler，不会走 MF 的机制，通过 mf-build-info.json 来完成资源下载，用于一些特定场景的性能优化
  getPrefetchHandler = (container: string) => {
    let handler = this.prefetchModuleHandlers.get(container);
    if (!handler) {
      handler = new PrefetchModuleHandler({ container, runtime: this.getRuntime() });
      this.prefetchModuleHandlers.set(container, handler);
    }
    return handler;
  };

  // 加载某个 container 的描述文件
  loadManifest = async (container: string) => {
    return this.getHandler(container)
      .register()
      .then(handler => handler.getManifest());
  };
  // 加载某个 container 的 entry js 并初始化好
  loadRemoteEntry = async (container: string) => {
    return this.getHandler(container)
      .loadEntry()
      .then(handler => handler.initShared());
  };
  // 加载某个 container 的 entry js 但不初始化好，留给 webpack 去控制调用 init 的时机
  loadRemoteEntryWithoutInit = async (container: string) => {
    return this.getHandler(container);
  };
  // 加载某个 container 下的 module
  loadRemoteModule = async (container: string, module: string) => {
    // 加载当前模块
    return this.getHandler(container).loadChunk(module);
  };

  // prefetch 某个 container 下的 module 的 js 资源，但是不执行
  prefetchRemoteModule = async (container: string, module: string) => {
    // 如果直接执行 pureLoadChunk，会触发 module 下的 js 资源的加载
    // 但是我们只需要加载 js 资源，不需要执行，所以这里不应该调用 pureLoadChunk
    // module 下的 js 资源只能通过 mf-build-info.json 来获取
    // 新写一个 Handler 用来管控这种加载
    return this.getPrefetchHandler(container).prefetchChunk(module);
  };

  /** 预加载相关 **/
  // 闲时触发加载剩余的 manifest
  requestIdleLoadManifest = (container: string | string[]) => {
    let containers: string[] = [];
    if (typeof container === 'string') {
      containers = [container];
    } else if (Array.isArray(container)) {
      containers = container;
    }

    const taskList = containers.map(k => () => this.loadManifest(k));

    return new Promise(resolve => {
      const manifests: Array<IRemoteModuleManifest> = [];
      // 做一个结果计数器，用于确定是否所有 task 都已经执行完
      let count = taskList.length;
      const runTaskQueue = (deadline: IdleDeadline) => {
        while ((deadline.timeRemaining() > 0 || deadline.didTimeout) && taskList.length) {
          const task = taskList.shift();

          if (task) {
            task().then(manifest => {
              if (manifest) {
                manifests.push(manifest);
              }
              count--;
              if (count <= 0) {
                resolve(manifests);
              }
            });
          }
        }

        if (taskList.length) {
          requestIdleCallback(runTaskQueue, { timeout: IDLE_TIMEOUT });
        }
      };
      requestIdleCallback(runTaskQueue, { timeout: IDLE_TIMEOUT });
    });
  };

  // 预加载 Entry
  idlePreloadEntry = () => {
    if (!this.preloadEntryHandler) {
      const unloadedEntryKeys = this.getUnloadedEntryKeys();
      const taskQueue = unloadedEntryKeys.map(key => this.getHandler(key).preloadEntry);
      this.preloadEntryHandler = new PreloadEntryHandler(taskQueue);
    }
    this.preloadEntryHandler.idlePreloadEntry();
  };

  emit = (name: string, payload?: EventPayload) => {
    this.eventEmitter.emit(name, payload);
  };
}

// 仅影响单例的创建
let getRoot = getRootDefault;
// 对单例的创建进行配置
export const configure = (options: IRemoteModuleManagerOptions) => {
  // override getRoot
  getRoot = options.getRoot || getRoot;
};

let singleton: RemoteModuleManager;
export function getSingleton() {
  if (!singleton) {
    singleton = new RemoteModuleManager({
      getRoot,
      servicesDiscovered: Object.assign(
        {},
        REMOTE_MODULE_MANIFEST_PATH, // isDev 或者 window.__MEEGO_REMOTE_SERVICE__ 注入失败时做兜底
        window.__MEEGO_REMOTE_SERVICE__,
        window.__MEEGO_REMOTE_SERVICE_DEVTOOL__,
      ),
    });

    /** online 环境若 window.__MEEGO_REMOTE_SERVICE__ 注入失败则打点统计 */
    if (Object.keys(window.__MEEGO_REMOTE_SERVICE__ || {})?.length === 0 && !isDev) {
      getSlardar().sendLog('warn', {
        event: 'remoteServiceInjectFail',
        error: `failed to inject window.__MEEGO_REMOTE_SERVICE__`,
      });
    }
  }
  return singleton;
}

// const servicesDiscoveredSample: IServicesDiscovered = {
//   '@meego-remote/onboarding': '/b/onboarding/remote-module/manifest.json',
//   /* ... */
// };
